import React, { useEffect, useState } from "react";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Mail, Bell } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function NotificationPreferences() {
  const { user, refreshMe } = useAuth();
  const [prefs, setPrefs] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/users/me/notification-preferences")
      .then(({ data }) => {
        if (!cancelled) setPrefs(data);
      })
      .catch((err) => {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const toggleEmail = async (next) => {
    setBusy(true);
    try {
      const { data } = await api.put("/users/me/notification-preferences", {
        email_notifications_enabled: next,
      });
      setPrefs(data);
      toast.success(`Email notifications ${next ? "enabled" : "disabled"}`);
      await refreshMe();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const mandatory = !prefs?.role_can_opt_out_of_mandatory;
  const emailEnabled = !!prefs?.email_notifications_enabled;

  return (
    <div data-testid="notification-preferences-page" className="mx-auto max-w-3xl px-6 py-8 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-[color:var(--page-fg)]">
          Notification preferences
        </h1>
        <p className="mt-1 text-sm text-[color:var(--page-fg-muted)]">
          Control where Goodwin Resolve reaches you outside of the in-app inbox.
        </p>
      </div>

      <div className="brand-card p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <span
              className="mt-1 flex h-9 w-9 items-center justify-center rounded-xl"
              style={{ backgroundColor: "rgba(20, 184, 184, 0.16)", color: "#5EE0E0" }}
            >
              <Mail className="h-4 w-4" />
            </span>
            <div>
              <h2 className="text-base font-semibold text-[color:var(--page-fg)]">Email notifications</h2>
              <p className="mt-1 text-xs text-[color:var(--page-fg-muted)]">
                Receive an email for case-assigned, follow-up-due, stale-case, and cadence
                reminders.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Label htmlFor="email-toggle" className="text-xs uppercase tracking-wider text-[color:var(--page-fg-muted)]">
              {emailEnabled ? "On" : "Off"}
            </Label>
            <Switch
              id="email-toggle"
              data-testid="email-notifications-toggle"
              checked={emailEnabled}
              onCheckedChange={toggleEmail}
              disabled={busy}
              aria-label="Toggle email notifications"
            />
          </div>
        </div>
        <div className="mt-4 rounded-xl border border-[color:var(--border-subtle)] p-4">
          <p className="text-xs text-[color:var(--page-fg-muted)]">
            <b>Mandatory deliveries</b> — high-risk SLA breaches and auto-escalations are
            sent to {user?.role === "hr" || user?.role === "compliance" ? "you" : "HR + Compliance"}{" "}
            regardless of this toggle.
          </p>
          {mandatory && (
            <div className="mt-2 flex flex-wrap gap-2">
              {prefs?.mandatory_types?.map((t) => (
                <Badge
                  key={t}
                  data-testid={`mandatory-type-${t}`}
                  className="rounded-full"
                  style={{
                    backgroundColor: "rgba(229, 77, 77, 0.14)",
                    color: "#FCA5A8",
                    border: "1px solid #E54D4D",
                  }}
                >
                  Mandatory · {t}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="brand-card p-6">
        <div className="flex items-start gap-3">
          <span
            className="mt-1 flex h-9 w-9 items-center justify-center rounded-xl"
            style={{ backgroundColor: "rgba(20, 184, 184, 0.16)", color: "#5EE0E0" }}
          >
            <Bell className="h-4 w-4" />
          </span>
          <div>
            <h2 className="text-base font-semibold text-[color:var(--page-fg)]">In-app notifications</h2>
            <p className="mt-1 text-xs text-[color:var(--page-fg-muted)]">
              Always on. Visit the Notifications page to mark items read.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
